import { ChangeEvent } from 'react';
import { faSearch, faSpinnerThird, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CategoryIcon from './CategoryIcon';
import { Category } from './types';

type Props = {
  categories: Category[];
  selected: Category;
  onSelect: (categoryId: string | null) => void;
  search: string;
  onSearchChange: (search: string) => void;
  isSearching: boolean;
};

const SELECT_ID = 'magzter-gold-category';
const SEARCH_ID = 'magzter-gold-search';

/**
 * The yellow band: which category you are in, and the box for searching within it.
 *
 * It carries the page's `<h1>`. The name is the category rather than the page because
 * the category is what the visitor asked for — and below `md`, where the sidebar is gone,
 * the select is that heading, which is why the two swap rather than stack.
 */
export default function GridHeader({
  categories,
  selected,
  onSelect,
  search,
  onSearchChange,
  isSearching,
}: Props) {
  const showClear = search !== '' && !isSearching;

  return (
    <div className="p-3 lg:p-5 mb-8 rounded-md bg-yellow-100 flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-3">
      <span className="flex items-center gap-3 mx-auto lg:mx-0">
        <CategoryIcon category={selected} isActive />

        <label htmlFor={SELECT_ID} className="hidden">
          Category
        </label>
        <select
          id={SELECT_ID}
          className="lg:hidden bg-transparent border-0 p-0 font-semibold text-slate-900 text-xl"
          value={selected.id ?? ''}
          // `=== ''` rather than `|| null`: the empty string is the option this page
          // adds for "no category", but "0" is a perfectly good third-party id and
          // truthiness would throw it away.
          onChange={(event: ChangeEvent<HTMLSelectElement>) =>
            onSelect(event.target.value === '' ? null : event.target.value)
          }
        >
          {categories.map((category) => (
            <option key={category.id ?? 'all'} value={category.id ?? ''}>
              {category.name}
            </option>
          ))}
        </select>

        <h1 className="hidden lg:inline-flex text-lg lg:text-2xl font-semibold tracking-tight py-3 pr-5">
          {selected.name}
        </h1>
      </span>

      <span className="flex-grow max-w-none lg:max-w-2xl relative">
        <label htmlFor={SEARCH_ID} className="hidden">
          Search {selected.name} titles
        </label>
        <input
          id={SEARCH_ID}
          type="search"
          value={search}
          placeholder={`Search ${selected.name} titles...`}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onSearchChange(event.target.value)}
          className="w-full py-2 px-4 pr-16 rounded-full border-2 border-yellow-300 focus:border-yellow-900 focus:ring-yellow-500"
        />

        {showClear && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => onSearchChange('')}
            className="absolute right-11 top-1/2 -translate-y-1/2 text-slate-600"
          >
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </button>
        )}

        <FontAwesomeIcon
          icon={isSearching && search !== '' ? faSpinnerThird : faSearch}
          spin={isSearching && search !== ''}
          className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-600 pointer-events-none"
        />
      </span>
    </div>
  );
}
